// Pezzi del pannello tracking spostati fuori da TrackingPanel.tsx (28/09,
// tetto 800 righe): la riga compatta della pagina ordine, la data e le icone.
import { CopyButton } from "@/features/ordini/components/OrderCard";
import type { TrackingResult } from "../api";
import { StepBadge } from "./StepBadge";

/** Pagina ordine, pannello chiuso: una riga sola con codice, stato e ultimo
 *  evento. Niente si perde: «Dettaglio» apre cronologia e colli. */
export function RigaTracking({
  code,
  result,
  loading,
  error,
  colli,
  titoloAggiorna,
  onRefresh,
  onApri,
  ritorno,
}: {
  code: string;
  result: TrackingResult | undefined;
  loading: boolean;
  error: string | null;
  colli: number;
  titoloAggiorna: string;
  onRefresh: () => void;
  onApri: () => void;
  /** Madre o figlia che torna al mittente: la riga diventa rossa. */
  ritorno?: TrackingResult;
}) {
  const events = result?.events ?? [];
  const ultimo = events.length > 0 ? events[events.length - 1] : undefined;
  const stato = loading
    ? "Leggo il tracking…"
    : error
      ? "Tracking non letto: apri il dettaglio"
      : ultimo
        ? [ultimo.status, ultimo.location, formatTimestamp(ultimo.timestamp)]
            .filter(Boolean)
            .join(" · ")
        : result?.unavailable
          ? "Tracking momentaneamente non disponibile"
          : "Non ancora affidata al corriere";
  return (
    <div
      className={[
        "flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md px-3 py-2",
        ritorno ? "border-2 border-red bg-red-soft" : "border border-line bg-paper",
      ].join(" ")}
    >
      {code ? (
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate font-mono text-[13px] font-semibold text-ink" title={code}>
            {code}
          </span>
          <CopyButton text={code} label="Copia codice tracking" />
        </span>
      ) : null}
      {ritorno ? (
        <StepBadge step={ritorno.current_step} small />
      ) : result?.current_step ? (
        <StepBadge step={result.current_step} small />
      ) : null}
      {/* min-w e non truncate: in una colonna stretta (contesto chat) lo stato
          va a capo su una riga sua invece di ridursi a «In tra…». */}
      <span className="min-w-[12rem] flex-1 break-words text-[12px] text-ink" title={stato}>
        {stato}
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          aria-label={titoloAggiorna}
          title={titoloAggiorna}
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-line bg-card text-ink enabled:hover:border-accent enabled:hover:bg-paper disabled:cursor-not-allowed"
        >
          <RefreshIcon spinning={loading} />
        </button>
        <button
          type="button"
          onClick={onApri}
          aria-expanded={false}
          className="inline-flex h-7 items-center gap-1 rounded-sm border border-line bg-card px-2.5 text-[11.5px] font-medium text-ink hover:border-accent hover:bg-paper"
        >
          Dettaglio{colli > 1 ? ` · ${colli} colli` : ""}
          <ChevronIcon open={false} />
        </button>
      </span>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Formatta un timestamp ISO 8601 come ``dd/mm/yyyy HH:mm`` (ora locale). Se
 *  il valore non e' parsabile, ritorna il raw (onesti: meglio mostrare il
 *  grezzo che una data sbagliata). */
export function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

// ── Icone SVG inline (stroke) — niente emoji come icone (regola estetica). ──

export function RefreshIcon({ spinning = false }: { spinning?: boolean }) {
  return (
    <svg
      className={[
        "h-3.5 w-3.5 shrink-0 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]",
        spinning ? "animate-spin" : "",
      ].join(" ")}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
    </svg>
  );
}

export function ExternalIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
    </svg>
  );
}

export function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={[
        "h-3.5 w-3.5 shrink-0 fill-none stroke-current stroke-[1.6] text-ink transition-transform [stroke-linecap:round] [stroke-linejoin:round]",
        open ? "rotate-90" : "",
      ].join(" ")}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function ClockIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function WarningIcon() {
  return (
    <svg
      className="h-4 w-4 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    </svg>
  );
}

export function SpinnerIcon() {
  return (
    <svg
      className="h-4 w-4 animate-spin fill-none stroke-current stroke-[2] [stroke-linecap:round]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" className="opacity-25" />
      <path d="M21 12a9 9 0 0 0-9-9" className="opacity-90" />
    </svg>
  );
}
